import React, { useMemo } from "react";
import { connect } from "react-redux";

import AreaTypes from "types/AreaTypes";
import QueueAreaData from "types/QueueAreaData";
import { QueueManagementOptions } from "types/QueueManagementOptions";

import MapArea from "components/MapArea";
import MapLabel from "components/MapLabel";
import MapPoint from "components/MapPoint";

import MapUtils from "services/map/utils";
// @ts-ignore - file is not written in TS.
import PedestrianStatisticsUtils from "services/pedestrianStatistics/utils";
import ParkkiTheme from "themes/ParkkiTheme";

type Props = {
  loading: boolean;
  currentFloor: number;
  options: {
    items: {
      area_id: string;
      reqFilters: { from: number; to: number };
      background: {
        value: number;
        total: number;
        out: number;
        in: number;
        config: {
          mainColor: string;
          unselectedColor: string;
          minColor: string;
          valueColors: {};
          unselectedValueColor: string;
        };
      };
    }[];
  };
  filters: {
    from: number;
    to: number;
  };
  map: {
    initialized: boolean;
    areas: AreaTypes[];
    selectedLayers: { id: string }[];
  };
  isAreaSelected?: Function;
  isLabelDisplayed?: Function;
  hasLabelBackground?: Function;
  onSelectLayers: Function;
  getLabelIcon?: Function;
  getLabel?: Function;
  getBackground?: Function;
  mapConfig: { labelsDisplayed?: boolean };
  zoom: number;
  pedestrianStatistics: {
    getBackofficeSettingsConditionWithValue: Function;
    queue: {
      data: QueueAreaData[];
    };
    config: { queue_management: QueueManagementOptions[] };
  };
};

function AreasLayers({
  currentFloor,
  filters,
  options,
  map,
  loading,
  isAreaSelected,
  isLabelDisplayed,
  hasLabelBackground,
  getBackground,
  getLabelIcon,
  getLabel,
  onSelectLayers,
  mapConfig,
  zoom,
  pedestrianStatistics,
}: Props): JSX.Element[] {
  const selectedLayers = useMemo(() => {
    return map?.selectedLayers || [];
  }, [map?.selectedLayers]);

  function handleAreaClick(area: AreaTypes) {
    const newSelectedLayer = {
      ...area,
      type: "area",
    };

    const alreadySelected = !!selectedLayers.find(
      (itm) => area?.id === itm?.id
    );

    const newSelectedLayers = alreadySelected
      ? selectedLayers.filter((itm) => area?.id !== itm?.id)
      : [...selectedLayers, newSelectedLayer];
    const isSelection = newSelectedLayers?.length > selectedLayers?.length;

    return onSelectLayers(newSelectedLayers, newSelectedLayer, isSelection);
  }

  function handleIsAreaSelected(area: AreaTypes): boolean {
    if (isAreaSelected) {
      return isAreaSelected(area);
    }

    return !!selectedLayers.find((itm) => area?.id === itm?.id);
  }

  function handleIsLabelDisplayed(area: AreaTypes) {
    if (isLabelDisplayed) {
      return (
        mapConfig?.labelsDisplayed &&
        isLabelDisplayed({ ...area, selected: handleIsAreaSelected(area) })
      );
    }

    return mapConfig?.labelsDisplayed;
  }

  function handleHasLabelBackground(area: AreaTypes) {
    if (hasLabelBackground) {
      return (
        mapConfig?.labelsDisplayed &&
        hasLabelBackground({ ...area, selected: handleIsAreaSelected(area) })
      );
    }

    return true;
  }

  function handleIsInteractive(area: AreaTypes) {
    return !["UNEQUIPPED"].includes(area?.apiType);
  }

  if (!map?.initialized || loading) {
    return [];
  }

  return map?.areas
    ?.filter((area) =>
      currentFloor || currentFloor === 0
        ? area.floor_level === currentFloor
        : area
    )
    .map((area) => {
      const selected = handleIsAreaSelected(area);
      const label = getLabel ? getLabel(area) : null;
      const formattedOptions = options?.items?.find((item) => {
        return item.area_id === area.id;
      });
      const background = getBackground
        ? getBackground(area)
        : formattedOptions?.background || area?.options?.background;

      if (area.apiType === "QUEUEMANAGEMENT") {
        const areaConfig = pedestrianStatistics.config?.queue_management.find(
          (queueManagementConfig: QueueManagementOptions) =>
            queueManagementConfig.area_id === area.area_id
        );

        let customBackground = { ...background, value: 0, total: 100 };

        if (areaConfig) {
          const color =
            PedestrianStatisticsUtils.getLabelBackgroundColorByAreaCondition(
              pedestrianStatistics,
              areaConfig,
              background,
              area
            );

          /* This is a workaround to always fill the background with the correct color
            based on the condition index and related percentage */
          customBackground = {
            ...background,
            config: {
              ...background?.config,
              valueColors: {
                0: { backgroundColor: color ?? ParkkiTheme.colors.green },
              },
            },
            value: 100,
            total: 100,
          };
        }

        return (
          <MapPoint
            bringToFront
            zoom={zoom}
            key={area.id}
            disabled={false}
            center={MapUtils.getNormalizedCoordinates(
              area.options?.point_location || []
            )}
            labelLocation={MapUtils.getNormalizedCoordinates(
              area.options?.location || []
            )}
            options={{
              ...(area?.options || {}),
              ...(formattedOptions || {}),
              withLabel: handleIsLabelDisplayed(area),
              withLabelBackground: handleHasLabelBackground({
                ...area,
                options: { ...area?.options, ...formattedOptions, background },
              }),
              apiType: area.apiType,
              background: customBackground,
            }}
            label={
              label ? (
                <MapLabel layer={area} getIcon={getLabelIcon} label={label} />
              ) : null
            }
            onClick={() => handleAreaClick(area)}
            selected={selected}
            interactive={handleIsInteractive(area)}
          />
        );
      }

      return (
        <MapArea
          key={area.id}
          disabled={
            area?.type === "UNEQUIPPED" || area?.apiType === "UNEQUIPPED"
          }
          positions={MapUtils.getNormalizedAreaCoordinates(area?.polygon || [])}
          options={{
            ...(area?.options || {}),
            ...formattedOptions,
            tooltipCoordinates: area?.display?.location,
            withLabel: handleIsLabelDisplayed(area),
            withLabelBackground: handleHasLabelBackground({
              ...area,
              options: { ...area?.options, ...formattedOptions },
            }),
            background,
          }}
          label={
            label ? (
              <MapLabel layer={area} getIcon={getLabelIcon} label={label} />
            ) : null
          }
          selected={selected}
          onClick={() => handleAreaClick(area)}
          interactive={handleIsInteractive(area)}
        />
      );
    });
}

const mapStateToProps = (state: any) => ({
  zoom: MapUtils.getZoom(state.Map),
  currentFloor: MapUtils.getFloor(state.Map),
  mapConfig: state.Map.config,
  pedestrianStatistics: state.PedestrianStatistics,
});

// @ts-ignore
export default connect(mapStateToProps)(AreasLayers);
